import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';

import { getRol, getToken, getUser } from '../../../api/Account';
import { getSaldoCarteraByGestorAsync } from '../../../api/Report';
import { getGestoresAsync } from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { formatearMonto } from '../../../helpers/formato';
import ReporteAgrupado from '../../../components/reportes/ReporteAgrupado';

const TODOS = 't';

// Cada fila es un gestor con sus créditos ya contados y sumados por el servidor.
// Tipo de crédito: al día (D), en mora (M), vigente (no vencido) y vencido (V).
const conteo = (key, label) => ({
	key,
	label,
	align: 'center',
	total: true,
	valor: (fila) => fila[key]
});

const monto = (key, label) => ({
	key,
	label,
	align: 'center',
	total: true,
	formatoTotal: formatearMonto,
	valor: (fila) => formatearMonto(fila[key]),
	excel: (fila) => fila[key]
});

// Porcentaje de los créditos del gestor; se calcula al armar las filas para poder ordenar.
const porcentaje = (key, label) => ({
	key,
	label,
	align: 'center',
	valor: (fila) => fila[key].toFixed(2),
	excel: (fila) => Number(fila[key].toFixed(2))
});

const COLUMNAS = [
	{ key: 'gestor', label: 'Gestor', align: 'left', texto: true, valor: (fila) => fila.gestor },
	conteo('noClients', 'No Clientes'),
	conteo('noCredits', 'No Creditos'),
	conteo('dia', 'Al Dia'),
	monto('montoDia', 'M. al Dia'),
	porcentaje('porcentajeDia', '% al Dia'),
	conteo('mora', 'Mora'),
	monto('montoMora', 'M. Mora'),
	porcentaje('porcentajeMora', '% Mora'),
	conteo('vigente', 'Vigente'),
	monto('montoVigente', 'M. Vigente'),
	porcentaje('porcentajeVigente', '% Vigente'),
	conteo('vencido', 'Vencido'),
	monto('montoVencido', 'M. Vencido'),
	porcentaje('porcentajeVencido', '% Vencido'),
	{
		key: 'creditoPromedio',
		label: 'Credito Prom.',
		align: 'center',
		valor: (fila) => formatearMonto(fila.creditoPromedio),
		excel: (fila) => fila.creditoPromedio
	},
	monto('saldoCartera', 'Saldo Cartera')
];

// El promedio de todos es el monto entregado total entre el total de créditos, no el
// promedio de los promedios de cada gestor.
const resumen = (filas) => {
	const creditos = filas.reduce((total, f) => total + f.noCredits, 0);
	const entregado = filas.reduce((total, f) => total + f.creditoPromedio * f.noCredits, 0);
	return [
		{
			label: 'Monto Creditos Promedio',
			valor: creditos > 0 ? formatearMonto(entregado / creditos) : '—'
		}
	];
};

const SaldosCarteraByGestor = () => {
	const [data, setData] = useState([]);
	const [gestorName, setGestorName] = useState('Todos');

	const { selectedGestor } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const manejarError = useCallback(
		(result) => {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
		},
		[setIsUnautorized]
	);

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			const [reporte, gestores] = await Promise.all([
				getSaldoCarteraByGestorAsync(token, selectedGestor === TODOS ? 0 : selectedGestor),
				selectedGestor === TODOS ? null : getGestoresAsync(token)
			]);
			setIsLoading(false);

			if (!reporte.statusResponse) {
				manejarError(reporte);
				return;
			}
			setData(reporte.data.result);

			if (selectedGestor !== TODOS) {
				// Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
				const gestor =
					gestores && gestores.statusResponse
						? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
						: null;
				setGestorName(
					gestor ? gestor.fullName : reporte.data.result[0]?.gestor ?? selectedGestor
				);
			}
		})();
	}, [token, selectedGestor, setIsLoading, manejarError]);

	// El reporte no trae un id por fila: se numera para tener una clave estable.
	const filas = useMemo(
		() =>
			data.map((fila, indice) => ({
				...fila,
				id: indice,
				porcentajeDia: (fila.dia / fila.noCredits) * 100,
				porcentajeMora: (fila.mora / fila.noCredits) * 100,
				porcentajeVigente: (fila.vigente / fila.noCredits) * 100,
				porcentajeVencido: (fila.vencido / fila.noCredits) * 100
			})),
		[data]
	);

	return (
		<ReporteAgrupado
			tituloReporte="Reporte de Saldos Cartera Por Gestor"
			nombreArchivo="SaldosCarteraByGestor"
			parametros={[{ label: 'Gestor Seleccionado', valor: gestorName }]}
			columnas={COLUMNAS}
			data={filas}
			unidad="gestor"
			unidadPlural="gestores"
			ordenInicial="gestor"
			resumen={resumen}
			contexto={{ title, slogan, icon, user, rol }}
		/>
	);
};

export default SaldosCarteraByGestor;
